import { supabase } from './supabase';
import type { Camp, CampFavorite } from '../types/database';
import { calculateDistance } from './distance';
import type { ProximitySearchRequest, ProximitySearchResponse, ProximityCamp, CampScheduleSearchRequest, CampScheduleSearchResponse } from '../types/search';
import { calculateAgeOnDate } from './ageCalculation';
import { getEarliestStartDate } from './campDateParsing';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

/**
 * Check if a camp matches age requirements based on child DOB(s)
 * Calculates age at camp start date and checks against camp's min_age/max_age
 * @param camp - Camp object with min_age, max_age, and dates_available
 * @param childDateOfBirths - Array of child DOBs (ISO date strings)
 * @returns true if at least one child matches the camp's age requirements
 */
function matchesAgeRequirements(
  camp: { min_age?: number | null; max_age?: number | null; dates_available?: string[] | null },
  childDateOfBirths: string[]
): boolean {
  // If no DOBs provided, skip age filtering
  if (!childDateOfBirths || childDateOfBirths.length === 0) {
    return true;
  }

  // If camp has no age requirements, include it
  if (camp.min_age === null && camp.max_age === null) {
    return true;
  }

  // If camp has no dates, we can't calculate age at camp start date
  // Use current date as fallback (not ideal, but better than excluding all camps without dates)
  const campStartDate = getEarliestStartDate(camp.dates_available) || new Date();

  // Check if at least one child matches the age requirements
  for (const childDOB of childDateOfBirths) {
    const ageAtCampStart = calculateAgeOnDate(childDOB, campStartDate);
    
    if (ageAtCampStart === null) {
      continue; // Skip invalid DOB
    }

    // Check if age falls within camp's age range
    const matchesMinAge = camp.min_age === null || ageAtCampStart >= camp.min_age;
    const matchesMaxAge = camp.max_age === null || ageAtCampStart <= camp.max_age;

    if (matchesMinAge && matchesMaxAge) {
      return true; // At least one child matches
    }
  }

  return false; // No children match the age requirements
}

async function retryOperation<T>(operation: () => Promise<T>, retries = MAX_RETRIES): Promise<T> {
  let lastError;
  
  for (let i = 0; i < retries; i++) {
    try {
      return await operation();
    } catch (error) {
      console.error(`Attempt ${i + 1} failed:`, error);
      lastError = error;
      
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, i)));
      }
    }
  }
  
  throw lastError;
}

export const api = {
  camps: {
    // Proximity search: bounding-box filter + client Haversine distance sort (migrates to Edge Function later)
    async proximitySearch(req: ProximitySearchRequest): Promise<ProximitySearchResponse> {
      const { latitude, longitude, radiusMiles, filters, searchQuery, limit = 20, offset = 0 } = req;
      
      // Extract child DOBs from filters
      const childDateOfBirths: string[] = [];
      if (filters?.childDateOfBirth) {
        childDateOfBirths.push(filters.childDateOfBirth);
      }
      if (filters?.childDateOfBirths && filters.childDateOfBirths.length > 0) {
        childDateOfBirths.push(...filters.childDateOfBirths);
      }

      return retryOperation(async () => {
        // Compute simple bounding box around user location
        const latDelta = radiusMiles / 69; // ~miles per degree lat
        const lngDelta = radiusMiles / (69 * Math.cos(latitude * Math.PI / 180));

        // Prefer server-side RPC for distance sort
        try {
          const { data, error } = await supabase.rpc('proximity_search', {
            lat: latitude,
            lon: longitude,
            radius_miles: radiusMiles,
            category_ids: filters?.categories || null,
            search_query: searchQuery || null,
            result_limit: limit,
            result_offset: offset,
          });
          if (error) {
            console.error('[API] RPC proximity_search error:', error);
            throw error;
          }
          console.log('[API] RPC proximity_search returned:', data?.length || 0, 'results');
          // Ensure distance_miles and relevance_score are numbers for each result
          let transformedData = (data || []).map((camp: any) => ({
            ...camp,
            distance_miles: parseFloat(camp.distance_miles) || 0,
            relevance_score: parseFloat(camp.relevance_score) || 0
          }));

          // Apply age filtering if child DOBs provided
          if (childDateOfBirths.length > 0) {
            console.log('[API] Applying age filtering for', childDateOfBirths.length, 'child(ren)');
            
            // RPC doesn't return dates_available, min_age, max_age - need to fetch them
            const campIds = transformedData.map(c => c.id);
            const { data: campAges, error: ageError } = await supabase
              .from('camps')
              .select('id, min_age, max_age, dates_available')
              .in('id', campIds);
            
            if (!ageError && campAges) {
              // Create a map for quick lookup
              const ageMap = new Map(campAges.map(c => [c.id, { min_age: c.min_age, max_age: c.max_age, dates_available: c.dates_available }]));
              
              // Merge age data into camps and filter
              transformedData = transformedData
                .map(camp => ({
                  ...camp,
                  ...ageMap.get(camp.id)
                }))
                .filter((camp: any) => matchesAgeRequirements(camp, childDateOfBirths));
            } else {
              console.warn('[API] Error fetching camp age data:', ageError);
              // If we can't fetch age data, skip age filtering (better than breaking)
            }
            
            console.log('[API] After age filtering:', transformedData.length, 'camps remain');
          }

          return { results: transformedData as ProximityCamp[] };
        } catch (rpcError) {
          console.warn('[API] RPC proximity_search unavailable, falling back to client compute', rpcError);
          let qb = supabase
            .from('camps')
            .select('*')
            .eq('active', true)
            .gte('latitude', latitude - latDelta)
            .lte('latitude', latitude + latDelta)
            .gte('longitude', longitude - lngDelta)
            .lte('longitude', longitude + lngDelta)
            .range(offset, offset + limit - 1);

          if (filters?.categories && filters.categories.length > 0) {
            const { data: categoryCamps } = await supabase
              .from('camp_category_relations')
              .select('camp_id')
              .in('category_id', filters.categories);
            const campIds = (categoryCamps || []).map((c: any) => c.camp_id);
            if (campIds.length === 0) return { results: [] };
            qb = qb.in('id', campIds);
          }

          const { data, error } = await qb;
          if (error) throw error;

          let withDistance = (data || [])
            .map((c: any) => {
              const dist = (c.latitude && c.longitude)
                ? calculateDistance(latitude, longitude, c.latitude, c.longitude)
                : Number.POSITIVE_INFINITY;
              return { ...(c as ProximityCamp), distance_miles: dist };
            })
            .filter(c => isFinite(c.distance_miles || Infinity))
            .sort((a, b) => (a.distance_miles! - b.distance_miles!));

          // Apply age filtering if child DOBs provided
          if (childDateOfBirths.length > 0) {
            console.log('[API] Applying age filtering for', childDateOfBirths.length, 'child(ren)');
            withDistance = withDistance.filter((camp: any) => matchesAgeRequirements(camp, childDateOfBirths));
            console.log('[API] After age filtering:', withDistance.length, 'camps remain');
          }

          return { results: withDistance };
        }
      });
    },
    async search({ query, location, category, ageRange, zipCode }: {
      query?: string;
      location?: { lat: number; lng: number; radius: number };
      category?: string;
      ageRange?: { min: number; max: number };
      zipCode?: string;
    } = {}) {
      return retryOperation(async () => {
        let queryBuilder = supabase
          .from('camps')
          .select(`
            *,
            camp_favorites!left(*),
            camp_images(*)
          `)
          .eq('active', true)
          .order('created_at', { ascending: false });

        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          queryBuilder = queryBuilder.eq('camp_favorites.user_id', user.id);
        }

        if (query) {
          queryBuilder = queryBuilder.or(`name.ilike.%${query}%,description.ilike.%${query}%`);
        }

        if (zipCode) {
          queryBuilder = queryBuilder.eq('zip_code', zipCode);
        } else if (location) {
          const lat = location.lat;
          const lng = location.lng;
          const radius = location.radius;
          
          queryBuilder = queryBuilder
            .gte('latitude', lat - radius/111.0)
            .lte('latitude', lat + radius/111.0)
            .gte('longitude', lng - radius/(111.0 * Math.cos(lat * Math.PI/180)))
            .lte('longitude', lng + radius/(111.0 * Math.cos(lat * Math.PI/180)));
        }

        if (category) {
          // Updated to use camp_category_relations instead of category_id
          queryBuilder = queryBuilder.eq('camp_category_relations.category_id', category);
        }

        if (ageRange) {
          queryBuilder = queryBuilder
            .gte('min_age', ageRange.min)
            .lte('max_age', ageRange.max);
        }

        const { data, error } = await queryBuilder;
        
        if (error) {
          console.error('Supabase query error:', error);
          throw error;
        }
        
        return data as (Camp & { camp_favorites: CampFavorite[] })[] || [];
      });
    },

    // New function for homepage filtering with pagination
    async fetchFilteredCamps({
      search = '',
      categories = [],
      radius = null,
      center = null,
      page = 1,
      pageSize = 12,
      sortBy = 'name',
      childDateOfBirths = []
    }: {
      search?: string;
      categories?: string[];
      radius?: number | null;
      center?: [number, number] | null;
      page?: number;
      pageSize?: number;
      sortBy?: 'name' | 'score' | 'distance';
      childDateOfBirths?: string[]; // Array of child DOBs for age filtering
    }) {
      return retryOperation(async () => {
        console.log('🔍 [API] Fetching filtered camps with params:', {
          search,
          categories: categories.length,
          radius,
          center,
          page,
          pageSize,
          sortBy
        });

        let queryBuilder = supabase
          .from('camps')
          .select(`
            *,
            camp_images(*),
            camp_favorites(*),
            camp_category_relations(
              camp_categories(
                id,
                name,
                description,
                category_type
              )
            )
          `)
          .eq('active', true);

        // Apply search filter
        if (search && search.trim()) {
          const searchTerms = search.trim();
          console.log('🔍 [API] Applying search filter:', searchTerms);
          // Use simple Supabase search - search in name field
          queryBuilder = queryBuilder.ilike('name', `%${searchTerms}%`);
        }

        // Apply category filters - Fixed approach
        if (categories.length > 0) {
          console.log('🔍 [API] Applying category filters:', categories);
          console.log('🔍 [API] Category IDs being filtered:', categories.map(id => ({ id, type: typeof id })));
          // Use a different approach: filter by camp IDs that have the selected categories
          // First, get the camp IDs that match the categories
          const { data: categoryCamps, error: categoryError } = await supabase
            .from('camp_category_relations')
            .select('camp_id')
            .in('category_id', categories);
          
          console.log('🔍 [API] Category camps query result:', { categoryCamps, categoryError });
          
          if (categoryError) {
            console.error('Error fetching category camps:', categoryError);
          } else if (categoryCamps && categoryCamps.length > 0) {
            const campIds = categoryCamps.map(c => c.camp_id);
            console.log('🔍 [API] Found camps matching categories:', campIds.length);
            console.log('🔍 [API] Camp IDs to filter by:', campIds);
            queryBuilder = queryBuilder.in('id', campIds);
          } else {
            console.log('🔍 [API] No camps match selected categories');
            // If no camps match categories, return empty result
            return [];
          }
        } else {
          console.log('🔍 [API] No categories selected, showing all camps');
        }

        // Apply radius/distance filter using bounding box
        if (radius && center && center[0] !== 0 && center[1] !== 0) {
          console.log('🔍 [API] Applying radius filter:', radius, 'miles around', center);
          const [lat, lng] = center;
          // Make the bounding box slightly larger to ensure we don't miss camps
          const latDelta = (radius * 1.2) / 69; // Increase by 20% for safety
          const lngDelta = (radius * 1.2) / (69 * Math.cos(lat * Math.PI / 180)); // Adjust for longitude
          
          console.log('🔍 [API] Bounding box deltas:', { latDelta, lngDelta });
          
          queryBuilder = queryBuilder
            .gte('latitude', lat - latDelta)
            .lte('latitude', lat + latDelta)
            .gte('longitude', lng - lngDelta)
            .lte('longitude', lng + lngDelta);
        }

        // Apply sorting
        if (sortBy === 'score') {
          queryBuilder = queryBuilder.order('score', { ascending: false });
        } else if (sortBy === 'distance' && center && center[0] !== 0 && center[1] !== 0) {
          // For distance sorting, we'll need to calculate distances in the query
          // For now, sort by name and handle distance sorting in the response
          queryBuilder = queryBuilder.order('name');
        } else {
          queryBuilder = queryBuilder.order('name');
        }

        // Apply pagination
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize - 1;
        queryBuilder = queryBuilder.range(startIndex, endIndex);

        const { data, error } = await queryBuilder;
        
        if (error) {
          console.error('Supabase query error:', error);
          throw error;
        }

        // Transform the data to match the expected structure
        let transformedCamps = data?.map(camp => ({
          ...camp,
          camp_categories: camp.camp_category_relations?.map((relation: any) => relation.camp_categories).filter(Boolean) || []
        })) || [];

        // Apply age filtering if child DOBs provided
        if (childDateOfBirths && childDateOfBirths.length > 0) {
          console.log('🔍 [API] Applying age filtering for', childDateOfBirths.length, 'child(ren)');
          transformedCamps = transformedCamps.filter(camp => matchesAgeRequirements(camp, childDateOfBirths));
          console.log('🔍 [API] After age filtering:', transformedCamps.length, 'camps remain');
        }

        console.log('🔍 [API] Fetched filtered camps:', transformedCamps.length);
        return transformedCamps as Camp[];
      });
    },

    // Get total count of camps that match the filters (for pagination)
    // Uses proximity_search RPC to match exact distance filtering like proximitySearch
    async getFilteredCampsCount({
      search = '',
      categories = [],
      radius = null,
      center = null
    }: {
      search?: string;
      categories?: string[];
      radius?: number | null;
      center?: [number, number] | null;
    }) {
      return retryOperation(async () => {
        // For proximity searches, use the same RPC as proximitySearch to get accurate count
        if (radius && center && center[0] !== 0 && center[1] !== 0) {
          try {
            // Use proximity_search RPC with a high limit to get all matching camps, then count
            const { data, error } = await supabase.rpc('proximity_search', {
              lat: center[0],
              lon: center[1],
              radius_miles: radius,
              category_ids: categories.length > 0 ? categories : null,
              result_limit: 10000, // High limit to get all results for counting
              result_offset: 0,
            });
            
            if (error) {
              console.warn('[API] Count: RPC proximity_search failed, falling back to bounding box', error);
              // Fall through to bounding box approach
            } else {
              // Filter by distance_miles to ensure we only count camps within radius
              const withinRadius = (data || []).filter((camp: any) => {
                const dist = parseFloat(camp.distance_miles) || Infinity;
                return dist <= radius;
              });
              
              // Apply search filter if needed (server-side RPC now handles search)
              if (search && search.trim()) {
                // Use RPC with search query to get accurate count
                const { data: searchData, error: searchError } = await supabase.rpc('proximity_search', {
                  lat: center[0],
                  lon: center[1],
                  radius_miles: radius,
                  category_ids: categories.length > 0 ? categories : null,
                  search_query: search.trim(),
                  result_limit: 10000, // High limit to count all matches
                  result_offset: 0,
                });
                
                if (!searchError && searchData) {
                  const filtered = (searchData || []).filter((camp: any) => {
                    const dist = parseFloat(camp.distance_miles) || Infinity;
                    return dist <= radius;
                  });
                  console.log('🔍 [API] Count: RPC with search returned', filtered.length, 'matches');
                  return filtered.length;
                }
              }
              
              console.log('🔍 [API] Count: RPC returned', withinRadius.length, 'camps within radius');
              return withinRadius.length;
            }
          } catch (rpcError) {
            console.warn('[API] Count: RPC proximity_search unavailable, falling back to bounding box', rpcError);
            // Fall through to bounding box approach
          }
        }
        
        // Fallback: Use bounding box approach (less accurate but works if RPC unavailable)
        let queryBuilder = supabase
          .from('camps')
          .select('id', { count: 'exact', head: true })
          .eq('active', true);

        // Apply search filter
        if (search && search.trim()) {
          const searchTerms = search.trim();
          console.log('🔍 [API] Count: Applying search filter:', searchTerms);
          queryBuilder = queryBuilder.ilike('name', `%${searchTerms}%`);
        }

        // Apply category filter
        if (categories && categories.length > 0) {
          console.log('🔍 [API] Count: Applying category filter:', categories);
          const { data: categoryCamps } = await supabase
            .from('camp_category_relations')
            .select('camp_id')
            .in('category_id', categories);
          
          if (categoryCamps && categoryCamps.length > 0) {
            const campIds = categoryCamps.map(rel => rel.camp_id);
            queryBuilder = queryBuilder.in('id', campIds);
          } else {
            return 0;
          }
        }

        // Apply radius/distance filter using bounding box (less accurate)
        if (radius && center && center[0] !== 0 && center[1] !== 0) {
          console.log('🔍 [API] Count: Applying radius filter (bounding box):', radius, 'miles around', center);
          const [lat, lng] = center;
          const latDelta = radius / 69;
          const lngDelta = radius / (69 * Math.cos(lat * Math.PI / 180));
          
          queryBuilder = queryBuilder
            .gte('latitude', lat - latDelta)
            .lte('latitude', lat + latDelta)
            .gte('longitude', lng - lngDelta)
            .lte('longitude', lng + lngDelta);
        }

        const { count, error } = await queryBuilder;
        
        if (error) {
          console.error('Supabase count error:', error);
          throw error;
        }

        return count || 0;
      });
    },

    async getFavorites() {
      return retryOperation(async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { data, error } = await supabase
          .from('camps')
          .select(`
            *,
            camp_favorites!inner(*),
            camp_images(*)
          `)
          .eq('camp_favorites.user_id', user.id)
          .eq('active', true)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching favorites:', error);
          throw error;
        }

        return data || [];
      });
    },

    async toggleFavorite(campId: string) {
      return retryOperation(async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        // Check if already favorited
        const { data: favorites, error: fetchError } = await supabase
          .from('camp_favorites')
          .select('id')
          .eq('camp_id', campId)
          .eq('user_id', user.id);

        if (fetchError) throw fetchError;

        if (favorites && favorites.length > 0) {
          // Remove favorite
          const { error } = await supabase
            .from('camp_favorites')
            .delete()
            .eq('id', favorites[0].id);

          if (error) throw error;
          return false;
        } else {
          // Add favorite
          const { error } = await supabase
            .from('camp_favorites')
            .insert({ camp_id: campId, user_id: user.id });

          if (error) throw error;
          return true;
        }
      });
    },

    /**
     * Search camps for schedule selection with favorites-first and proximity sorting
     * Used by SmartCampSelector component
     */
    async searchCampsForSchedule({
      query,
      userLocation,
      limit = 15,
      offset = 0,
      excludeCampIds = []
    }: CampScheduleSearchRequest): Promise<CampScheduleSearchResponse> {
      return retryOperation(async () => {
        const { data: { user } } = await supabase.auth.getUser();
        
        // If no query and no location, return favorites first
        if (!query || query.length < 3) {
          if (user) {
            // First, fetch favorite camp IDs
            const { data: favoriteIds, error: favIdsError } = await supabase
              .from('camp_favorites')
              .select('camp_id')
              .eq('user_id', user.id);

            if (favIdsError) {
              console.error('Error fetching favorite IDs:', favIdsError);
              return { results: [], isFavorites: true };
            }

            const campIds = favoriteIds?.map(f => f.camp_id) || [];
            
            if (campIds.length === 0) {
              return { results: [], isFavorites: true };
            }

            // Then fetch the full camp details for those IDs
            const { data: favorites, error: favError } = await supabase
              .from('camps')
              .select(`
                id,
                name,
                city,
                state,
                latitude,
                longitude
              `)
              .eq('active', true)
              .in('id', campIds)
              .order('name')
              .limit(limit);

            if (favError) {
              console.error('Error fetching favorites:', favError);
              return { results: [], isFavorites: true };
            }

            return { 
              results: (favorites || []).map(camp => ({ ...camp, is_favorite: true })),
              isFavorites: true 
            };
          }
          return { results: [], isFavorites: false };
        }

        // If query has 3+ characters, use proximity search or text search
        if (userLocation) {
          // Use proximity search with text query
          try {
            const { data, error } = await supabase.rpc('proximity_search', {
              lat: userLocation.lat,
              lon: userLocation.lng,
              radius_miles: 50, // Default radius for schedule search
              category_ids: null,
              search_query: query,
              result_limit: limit,
              result_offset: offset,
            });

            if (error) {
              console.error('RPC proximity_search error:', error);
              throw error;
            }

            let results = (data || []).map((camp: any) => ({
              ...camp,
              distance_miles: parseFloat(camp.distance_miles) || 0,
            }));

            // Filter out excluded camps
            if (excludeCampIds.length > 0) {
              results = results.filter((camp: any) => !excludeCampIds.includes(camp.id));
            }

            // Mark favorites if user is authenticated
            if (user) {
              const { data: favData } = await supabase
                .from('camp_favorites')
                .select('camp_id')
                .eq('user_id', user.id)
                .in('camp_id', results.map((c: any) => c.id));

              const favoriteIds = new Set(favData?.map(f => f.camp_id) || []);
              results = results.map((camp: any) => ({
                ...camp,
                is_favorite: favoriteIds.has(camp.id)
              }));
            }

            return { results, isFavorites: false };
          } catch (error) {
            console.warn('Proximity search failed, falling back to text search', error);
          }
        }

        // Fallback to text search (no location)
        let queryBuilder = supabase
          .from('camps')
          .select(`
            id,
            name,
            city,
            state,
            latitude,
            longitude
          `)
          .eq('active', true)
          .ilike('name', `%${query}%`)
          .order('name');

        // Filter out excluded camps (UUIDs are validated by TypeScript interface)
        if (excludeCampIds.length > 0) {
          queryBuilder = queryBuilder.not('id', 'in', `(${excludeCampIds.join(',')})`);
        }

        queryBuilder = queryBuilder.range(offset, offset + limit - 1);

        const { data, error } = await queryBuilder;

        if (error) {
          console.error('Text search error:', error);
          throw error;
        }

        let results = data || [];

        // Mark favorites if user is authenticated
        if (user && results.length > 0) {
          const { data: favData } = await supabase
            .from('camp_favorites')
            .select('camp_id')
            .eq('user_id', user.id)
            .in('camp_id', results.map(c => c.id));

          const favoriteIds = new Set(favData?.map(f => f.camp_id) || []);
          results = results.map(camp => ({
            ...camp,
            is_favorite: favoriteIds.has(camp.id)
          }));
        }

        return { results, isFavorites: false };
      });
    },

    async getCampBySlug(slug: string, isAdmin = false) {
      return retryOperation(async () => {
        let queryBuilder = supabase
          .from('camps')
          .select(`
            *,
            camp_images(*),
            camp_favorites(*),
            camp_category_relations(
              camp_categories(
                id,
                name,
                description,
                category_type
              )
            )
          `)
          .eq('slug', slug);

        // Only filter by active status if not admin
        if (!isAdmin) {
          queryBuilder = queryBuilder.eq('active', true);
        }

        const { data, error } = await queryBuilder.maybeSingle();

        if (error) {
          console.error('Supabase query error:', error);
          throw error;
        }

        if (!data) {
          console.error(`Camp not found with slug: ${slug}`);
          throw new Error(`Camp not found with slug: ${slug}`);
        }

        // Transform the data to match the expected structure
        const transformedCamp = {
          ...data,
          camp_categories: data.camp_category_relations?.map((relation: any) => relation.camp_categories).filter(Boolean) || []
        };

        return transformedCamp as Camp;
      });
    }
  },

  // New API functions for Camps by City navigation
  locations: {
    async getStates() {
      return retryOperation(async () => {
        console.log('[API] getStates: Starting query...');
        
        // Use pagination to get ALL camps (Supabase default limit is 1000)
        // Fetch in batches to ensure we get all camps
        let allCamps: any[] = [];
        let from = 0;
        const batchSize = 1000;
        let hasMore = true;

        while (hasMore) {
          const { data, error } = await supabase
            .from('camps')
            .select('state')
            .eq('active', true)
            .order('state')
            .range(from, from + batchSize - 1);

          if (error) {
            console.error('[API] getStates: Error fetching states:', error);
            throw error;
          }

          if (data && data.length > 0) {
            allCamps = allCamps.concat(data);
            from += batchSize;
            hasMore = data.length === batchSize; // If we got a full batch, there might be more
          } else {
            hasMore = false;
          }
        }
        
        console.log(`[API] getStates: Fetched ${allCamps.length} total camps from database (in ${Math.ceil(allCamps.length / batchSize)} batches)`);

        // Import the state conversion function
        const { convertStateAbbreviationToFullName } = await import('./campUtils');

        // Get unique states with camp counts, normalizing state names (CA -> California)
        const stateCounts = allCamps.reduce((acc: Record<string, number>, camp: any) => {
          // Only count camps with valid state values
          if (camp.state && camp.state.trim() !== '') {
            // Normalize state name (convert abbreviations to full names)
            const normalizedState = convertStateAbbreviationToFullName(camp.state.trim()) || camp.state.trim();
            // Ensure we're using the normalized state consistently
            const key = normalizedState.trim();
            if (key) {
              acc[key] = (acc[key] || 0) + 1;
            }
          }
          return acc;
        }, {});
        
        // Debug: Log raw state values to see what we're getting
        const uniqueRawStates = [...new Set(allCamps.map((c: any) => c.state).filter(Boolean))];
        console.log(`[API] getStates: Raw unique states from DB (${uniqueRawStates.length} total):`, uniqueRawStates.slice(0, 30));

        // Convert to array format for easier use, filtering out invalid entries
        const states = Object.entries(stateCounts)
          .filter(([state]) => state && state.trim() !== '') // Filter out empty states
          .map(([state, count]) => {
            const slug = state.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
            return {
              state,
              count,
              slug
            };
          })
          .filter((stateInfo) => stateInfo.slug && stateInfo.slug.length > 0); // Filter out invalid slugs

        const sortedStates = states.sort((a, b) => a.state.localeCompare(b.state));
        
        // Debug logging to help identify missing states
        console.log(`[API] getStates: ✅ Found ${sortedStates.length} states:`, sortedStates.map(s => `${s.state} (${s.count} camps)`));
        const hasTexas = sortedStates.some(s => s.state.toLowerCase().includes('texas'));
        console.log(`[API] getStates: 🔍 Texas found: ${hasTexas ? 'YES ✅' : 'NO ❌'}`);
        if (!hasTexas) {
          console.warn(`[API] getStates: ⚠️ Texas missing! Total camps processed: ${allCamps.length}`);
          // Log raw Texas state values from database
          const texasCamps = allCamps.filter((c: any) => c.state && (c.state.toLowerCase().includes('texas') || c.state.toLowerCase() === 'tx'));
          console.warn(`[API] getStates: 🔍 Raw Texas camps in data (${texasCamps.length} found):`, texasCamps.slice(0, 10).map((c: any) => c.state));
          if (texasCamps.length > 0) {
            // Check what the conversion function does with these
            const { convertStateAbbreviationToFullName } = await import('./campUtils');
            const converted = texasCamps.map((c: any) => ({
              raw: c.state,
              converted: convertStateAbbreviationToFullName(c.state.trim())
            }));
            console.warn(`[API] getStates: 🔍 Texas state conversions:`, converted.slice(0, 10));
          }
        }
        
        return sortedStates;
      });
    },

    async getCitiesByState(state: string) {
      return retryOperation(async () => {
        const { data, error } = await supabase
          .from('camps')
          .select('city, state')
          .eq('active', true)
          .eq('state', state)
          .order('city');

        if (error) {
          console.error('Error fetching cities:', error);
          throw error;
        }

        // Get unique cities with camp counts
        const cityCounts = data.reduce((acc: Record<string, number>, camp: any) => {
          acc[camp.city] = (acc[camp.city] || 0) + 1;
          return acc;
        }, {});

        // Convert to array format for easier use
        const cities = Object.entries(cityCounts).map(([city, count]) => ({
          city,
          state,
          count,
          slug: city.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')
        }));

        return cities.sort((a, b) => a.city.localeCompare(b.city));
      });
    },

    async getCampsByCity(city: string, state: string) {
      return retryOperation(async () => {
        const { data, error } = await supabase
          .from('camps')
          .select(`
            *,
            camp_images(*),
            camp_favorites(*),
            camp_category_relations(
              camp_categories(
                id,
                name,
                description,
                category_type
              )
            )
          `)
          .eq('active', true)
          .eq('city', city)
          .eq('state', state)
          .order('name');

        if (error) {
          console.error('Error fetching camps by city:', error);
          throw error;
        }

        // Transform the data to match the expected structure
        const transformedCamps = data.map((camp: any) => ({
          ...camp,
          camp_categories: camp.camp_category_relations?.map((relation: any) => relation.camp_categories).filter(Boolean) || []
        }));

        return transformedCamps as Camp[];
      });
    }
  }
};